"use client";

import Link from "next/link";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { UserMenu } from "@/components/layout/user-menu";
import { LanguageSwitcher } from "@/components/layout/language-switcher";
import { useCart } from "@/lib/cart-context";
import { GlobalSearch } from "@/components/global-search";

function CartLink({ onClick }: { onClick?: () => void }) {
  const { totalCount } = useCart();
  return (
    <Link
      href="/cart"
      onClick={onClick}
      aria-label={`Cart, ${totalCount} item${totalCount === 1 ? "" : "s"}`}
      className="relative flex h-9 w-9 items-center justify-center rounded-full border border-ink-border bg-white/55 text-ivory transition-colors hover:border-gold/40 hover:bg-white"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {totalCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-semibold text-ink">
          {totalCount}
        </span>
      )}
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const t = useTranslations("nav");
  const tc = useTranslations("categories");
  const showMobileSearch =
    pathname === "/" ||
    pathname === "/profiles" ||
    pathname.startsWith("/profiles/") ||
    pathname === "/profiles" ||
    pathname.startsWith("/profiles/") ||
    pathname === "/music" ||
    pathname === "/sports" ||
    pathname === "/culture" ||
    pathname === "/community" ||
    pathname.startsWith("/events/") ||
    pathname === "/cities" ||
    pathname.startsWith("/cities/");

  const navLinks = [
    {
      href: "/music",
      label: tc("music.label"),
      activeClass: "bg-gold text-ink",
      desktopActiveClass: "text-ivory",
      accent: "var(--gold)",
      dotClass: "bg-gold",
    },
    {
      href: "/sports",
      label: tc("sports.label"),
      activeClass: "bg-green text-white",
      desktopActiveClass: "text-ivory",
      accent: "var(--green)",
      dotClass: "bg-green",
    },
    {
      href: "/culture",
      label: tc("culture.label"),
      activeClass: "bg-red-soft text-white",
      desktopActiveClass: "text-ivory",
      accent: "var(--red-soft)",
      dotClass: "bg-red-soft",
    },
    {
      href: "/community",
      label: tc("community.label"),
      activeClass: "bg-sky text-white",
      desktopActiveClass: "text-ivory",
      accent: "var(--sky)",
      dotClass: "bg-sky",
    },
    {
      href: "/cities",
      label: t("cities"),
      activeClass: "bg-sky text-white",
      desktopActiveClass: "text-ivory",
      accent: "var(--sky)",
      dotClass: "bg-sky",
    },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-ink-border/75 bg-surface shadow-[0_12px_30px_-28px_rgb(var(--shadow-color))]">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Link
          href="/"
          className="group flex shrink-0 items-center gap-2"
          onClick={() => setOpen(false)}
        >
          <span className="relative h-2.5 w-2.5 rounded-full bg-[linear-gradient(135deg,var(--green),var(--gold),var(--red-soft))] shadow-[0_0_14px_color-mix(in_oklab,var(--gold)_55%,transparent)] transition-transform group-hover:scale-125" />
          <span className="font-display text-xl font-semibold tracking-tight text-ivory">
            edaga<span className="text-green">.events</span>
          </span>
        </Link>

        <GlobalSearch
          variant="header"
          className="hidden min-w-44 max-w-sm flex-1 lg:block"
        />

        <nav
          aria-label="Primary navigation"
          className="hidden shrink-0 items-center gap-3 lg:flex xl:gap-5"
        >
          {navLinks.map((link) => {
            const active = pathname === link.href || pathname.startsWith(`${link.href}/`);
            return (
              <Link
                key={link.href}
                href={link.href}
                aria-current={active ? "page" : undefined}
                style={
                  active
                    ? {
                        backgroundColor: `color-mix(in srgb, ${link.accent} 12%, white)`,
                      }
                    : undefined
                }
                className={cn(
                  "relative flex h-10 items-center justify-center gap-1.5 px-3 text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold xl:text-sm",
                  active
                    ? link.desktopActiveClass
                    : "text-ivory-muted hover:bg-white/55 hover:text-ivory"
                )}
              >
                {active && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 top-0 h-[3px]"
                    style={{ backgroundColor: link.accent }}
                  />
                )}
                <span className={cn("h-1.5 w-1.5 rounded-full", link.dotClass)} />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto hidden shrink-0 items-center gap-2 lg:flex">
          <CartLink />
          <LanguageSwitcher variant="desktop" />
          <UserMenu variant="desktop" />
        </div>

        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <Link
            href="/submit-event"
            aria-label={t("createEvent")}
            onClick={() => setOpen(false)}
            className="group inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-green/45 bg-green/10 px-2 text-xs font-semibold text-green transition-all hover:border-green hover:bg-green hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green min-[390px]:px-3"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green text-sm leading-none text-white transition-colors group-hover:bg-white group-hover:text-green" aria-hidden="true">
              +
            </span>
            <span className="hidden min-[390px]:inline">{t("createEvent")}</span>
          </Link>
          <CartLink />
          <button
            type="button"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-ink-border bg-white/55 text-ivory"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              {open ? (
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {showMobileSearch && (
        <div className="border-t border-ink-border/80 bg-surface lg:hidden">
          <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6">
            <GlobalSearch variant="header" />
          </div>
        </div>
      )}

      <div
        className={cn(
          "grid overflow-hidden border-t border-ink-border/80 bg-surface transition-[grid-template-rows] duration-300 ease-out lg:hidden",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <nav className="flex flex-col gap-1 px-4 py-3">
            {navLinks.map((link) => {
              const active = pathname === link.href || pathname.startsWith(`${link.href}/`);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={active ? "page" : undefined}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
                    active ? link.activeClass : "text-ivory-muted hover:bg-ink-raised hover:text-ivory"
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-white/85" : link.dotClass)} />
                  {link.label}
                </Link>
              );
            })}
            <LanguageSwitcher variant="mobile" />
            <div className="mt-1 border-t border-ink-border/70 pt-2">
              <UserMenu variant="mobile" onNavigate={() => setOpen(false)} />
            </div>
          </nav>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1.5 bg-[linear-gradient(90deg,var(--green),var(--gold),var(--red-soft),var(--sky),var(--green))]" />
    </header>
  );
}
